import { Component, EventEmitter, Input, Output } from '@angular/core';
import { FarmDTO } from '../../../models/farm';
import { VarietySlice } from '../operation.service';

@Component({
  selector: 'kw-farm-card',
  template: `
    <div class="farm-card" (click)="open.emit()">
      <div class="fc-top">
        <div class="fc-badge" [style.background]="badgeColor">{{ badgeText }}</div>
        <div class="fc-title">
          <h3>{{ farm?.name }}</h3>
          <div class="loc" *ngIf="locationLine">{{ locationLine }}</div>
          <div class="cust" *ngIf="farm?.customerName">
            <span class="cust-l">Customer</span> {{ farm.customerName }}
          </div>
        </div>
      </div>
      <div class="fc-stats">
        <div class="fc-stat"><div class="n">{{ acres | number:'1.0-0' }}</div><div class="l">GPS acres</div></div>
        <div class="fc-stat"><div class="n">{{ blockCount }}</div><div class="l">Blocks</div></div>
        <div class="fc-stat"><div class="n">{{ assetCount }}</div><div class="l">Assets</div></div>
      </div>
      <div class="fc-bar-wrap" *ngIf="varieties?.length">
        <div class="fc-bar-lbl"><span>Acreage by variety</span><span>{{ varieties.length }} varieties</span></div>
        <div class="fc-bar">
          <span *ngFor="let v of varieties" [style.width.%]="pct(v)" [style.background]="v.color"></span>
        </div>
        <div class="fc-legend">
          <div class="lg" *ngFor="let v of varieties.slice(0, 4)">
            <span class="d" [style.background]="v.color"></span><b>{{ v.name }}</b>
            <span class="ac">{{ v.acres | number:'1.0-0' }}ac</span>
          </div>
          <div class="lg" *ngIf="varieties.length > 4"><span class="ac">+{{ varieties.length - 4 }} more</span></div>
        </div>
      </div>
      <div class="fc-foot">
        <span class="fm" *ngIf="fieldman">Fieldman <b>{{ fieldman }}</b></span>
        <span class="fm" *ngIf="!fieldman">&nbsp;</span>
        <span class="go">View operation &rarr;</span>
      </div>
    </div>
  `,
  styles: [`
    .farm-card { background: #fff; border: 1px solid #e4e9ef; border-radius: 14px;
      box-shadow: 0 1px 2px rgba(16,32,55,.04), 0 2px 8px rgba(16,32,55,.05);
      overflow: hidden; cursor: pointer; display: flex; flex-direction: column;
      transition: transform .14s ease, box-shadow .14s ease, border-color .14s ease; }
    .farm-card:hover { transform: translateY(-3px); box-shadow: 0 6px 24px rgba(16,32,55,.12); border-color: #cfdae6; }
    .fc-top { padding: 16px 17px 13px; display: flex; align-items: flex-start; gap: 12px; }
    .fc-badge { width: 46px; height: 46px; border-radius: 11px; flex: none; display: grid; place-items: center;
      color: #fff; font-weight: 700; font-size: 15px; letter-spacing: .3px; }
    .fc-title { min-width: 0; flex: 1; }
    .fc-title h3 { margin: 0; font-size: 16.5px; font-weight: 700; letter-spacing: -.2px; }
    .fc-title .loc { color: #64748b; font-size: 12.5px; margin-top: 2px; }
    .fc-title .cust { color: #64748b; font-size: 12px; margin-top: 4px; }
    .fc-title .cust .cust-l { font-size: 10px; font-weight: 700; text-transform: uppercase; letter-spacing: .4px; color: #94a3b8; margin-right: 5px; }
    .fc-stats { display: grid; grid-template-columns: repeat(3, 1fr); border-top: 1px solid #eef2f6; border-bottom: 1px solid #eef2f6; }
    .fc-stat { padding: 12px 15px; border-right: 1px solid #eef2f6; }
    .fc-stat:last-child { border-right: none; }
    .fc-stat .n { font-size: 19px; font-weight: 700; letter-spacing: -.4px; line-height: 1; font-variant-numeric: tabular-nums; }
    .fc-stat .l { font-size: 11px; color: #64748b; margin-top: 4px; font-weight: 600; }
    .fc-bar-wrap { padding: 14px 17px 7px; }
    .fc-bar-lbl { display: flex; justify-content: space-between; font-size: 11px; color: #64748b; font-weight: 600; margin-bottom: 7px; }
    .fc-bar { height: 9px; border-radius: 5px; overflow: hidden; display: flex; background: #eef2f6; }
    .fc-bar span { height: 100%; }
    .fc-legend { display: flex; flex-wrap: wrap; gap: 4px 12px; padding: 10px 0 8px; }
    .lg { display: flex; align-items: center; gap: 6px; font-size: 11.5px; color: #17212e; }
    .lg .d { width: 9px; height: 9px; border-radius: 3px; flex: none; }
    .lg b { font-weight: 600; } .lg .ac { color: #94a3b8; font-weight: 500; }
    .fc-foot { margin-top: auto; padding: 11px 17px; border-top: 1px solid #eef2f6; display: flex;
      align-items: center; justify-content: space-between; background: #fafbfc; }
    .fc-foot .fm { font-size: 12px; color: #64748b; } .fc-foot .fm b { color: #17212e; font-weight: 600; }
    .fc-foot .go { font-size: 12.5px; color: #1b6f45; font-weight: 700; }
  `]
})
export class FarmCardComponent {
  @Input() farm: FarmDTO;
  @Input() acres = 0;
  @Input() blockCount = 0;
  @Input() assetCount = 0;
  @Input() varieties: VarietySlice[] = [];
  @Output() open = new EventEmitter<void>();

  get badgeText(): string {
    if (this.farm?.shortName) {
      return this.farm.shortName.substring(0, 3).toUpperCase();
    }
    return (this.farm?.name || '?').substring(0, 2).toUpperCase();
  }

  get badgeColor(): string {
    return this.varieties && this.varieties.length ? this.varieties[0].color : '#253d5b';
  }

  get locationLine(): string {
    const parts = [this.farm?.city, this.farm?.state].filter(Boolean);
    return parts.join(', ');
  }

  get fieldman(): string {
    const b = (this.farm?.blocks || []).find(x => !!x.fieldman);
    return b ? b.fieldman : '';
  }

  pct(v: VarietySlice): number {
    return this.acres > 0 ? (v.acres / this.acres) * 100 : 0;
  }
}
